import type { Website } from 'types';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { useAuth } from 'hooks/useAuth';
import { useWebsiteDetails } from '@/state/useWebsiteDetails';
import { can } from 'definitions/capabilities';
import { Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from 'helpers/cn';
import type { TrackingMethodChoice } from '../tracking-methods';
import { useTrackingMethodChoice } from '../use-tracking-method-choice';
import { ServerTrackingConfirm } from '../ServerTrackingConfirm';
import { DataFeedsSection } from './DataFeedsSection';
import { LearningsPastTests } from './LearningsPastTests';

type WebsiteSettingsModalProps = {
  website: Website;
  /** The owning account has opted out of contributing learnings, which overrides the website's own switch. */
  accountOptedOut: boolean;
  /** A product tour anchor for the trigger. */
  tourAnchor?: string;
};

type TrackingMethod = TrackingMethodChoice;

/** Chosen when a website has no default; the pipeline measures 'purchase' regardless. */
const NO_DEFAULT = '__none__';

const SELECT_TRIGGER = 'border-line bg-field text-foreground h-9 rounded-lg text-body';

const CURRENCIES = [
  { value: 'USD', label: 'USD — US Dollar' },
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'GBP', label: 'GBP — British Pound' },
  { value: 'SEK', label: 'SEK — Swedish Krona' },
  { value: 'NOK', label: 'NOK — Norwegian Krone' },
  { value: 'DKK', label: 'DKK — Danish Krone' },
  { value: 'CHF', label: 'CHF — Swiss Franc' },
  { value: 'CAD', label: 'CAD — Canadian Dollar' },
  { value: 'AUD', label: 'AUD — Australian Dollar' },
  { value: 'JPY', label: 'JPY — Japanese Yen' },
];

type ObjectiveSelectProps = {
  websiteId: number;
  value: string;
  onChange: (value: string) => void;
};

/**
 * The website's default objective, chosen from its primary-eligible pool.
 *
 * The details screen is not routed — menu-links.tsx defines only /websites/list and
 * /websites/google-analytics — so this modal is the only place this can be set. It is a
 * select rather than the free text it used to be: a typed name matching no event reports
 * zero conversions with no indication why.
 *
 * Its own component so that useWebsiteDetails mounts with the dialog rather than with the
 * row. The list renders one modal per website and hooks run even while the dialog is
 * closed, so keeping the fetch in the parent added a useClient and two useQuery
 * subscriptions to every row for data nobody was looking at.
 *
 * This is not what causes the list's "setState while rendering a different component"
 * warning: that reproduces with master's version of this file and of the list, so it
 * predates this work. useClient assigns a module-level onError closure during render
 * (hooks/useClient.ts), which is the likely source.
 */
const ObjectiveSelect = ({ websiteId, value, onChange }: ObjectiveSelectProps) => {
  const { eventGoals } = useWebsiteDetails(websiteId);
  const primaryOptions = (eventGoals ?? []).filter((goal) => goal.role === 'primary').map((goal) => goal.name ?? '');

  // A value saved before this became a select, or one whose event has since been demoted,
  // still has to be selectable or saving an unrelated field would discard it.
  const options = value !== NO_DEFAULT && !primaryOptions.includes(value) ? [value, ...primaryOptions] : primaryOptions;

  return (
    <div className="space-y-2">
      <span className="micro-label block">Default primary objective</span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className={SELECT_TRIGGER + ' w-[240px]'}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NO_DEFAULT}>Not set — assume purchase</SelectItem>
          {options.map((name) => (
            <SelectItem key={name} value={name}>
              {name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="supporting m-0">
        {primaryOptions.length === 0
          ? 'Map an event as a primary objective under Event Goals to choose one here.'
          : 'Used for experiments that do not choose their own objective.'}
      </p>
    </div>
  );
};

export const WebsiteSettingsModal = ({ website, accountOptedOut, tourAnchor }: WebsiteSettingsModalProps) => {
  const apiClient = useClient();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canManageLearnings = can(user, 'learnings:manage');

  const [open, setOpen] = useState(false);
  const [currency, setCurrency] = useState(website.defaultCurrency || 'USD');
  const [trackingMethod, setTrackingMethod] = useState<TrackingMethod>(website.trackingMethod ?? 'none');
  const [conversionEvent, setConversionEvent] = useState(website.conversionEvent ?? NO_DEFAULT);
  const [visitorIdEnabled, setVisitorIdEnabled] = useState(website.visitorIdEnabled ?? false);
  const [learningsEnabled, setLearningsEnabled] = useState(website.learningsEnabled ?? false);
  const [readScreenshots, setReadScreenshots] = useState(website.learningsReadScreenshots ?? true);
  const [contributesLearnings, setContributesLearnings] = useState(!(website.learningsContributionOptOut ?? false));
  const choice = useTrackingMethodChoice({ website, enabled: open, setTrackingMethod });

  const handleSave = async () => {
    try {
      await apiClient.put(`/websites/${website.id}/settings`, {
        defaultCurrency: currency,
        trackingMethod: trackingMethod === 'none' ? null : trackingMethod,
        conversionEvent: conversionEvent === NO_DEFAULT ? null : conversionEvent,
        visitorIdEnabled,
        learningsEnabled,
        learningsReadScreenshots: readScreenshots,
        learningsContributionOptOut: !contributesLearnings,
      });
    } catch {
      // useClient has shown the server's reason (a Shopify store refusing Server-side, say);
      // the dialog stays open with the choice so it can be changed.
      return;
    }
    queryClient.invalidateQueries({ queryKey: ['WEBSITE', website.id] });
    setOpen(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label={`Settings for ${website.url}`}
            title={`Settings for ${website.url}`}
            className="text-dim hover:text-foreground hover:bg-hover h-8 w-8 rounded-lg"
            data-tour={tourAnchor}
          >
            <Settings className="h-4 w-4" />
          </Button>
        </DialogTrigger>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>Settings</DialogTitle>
            <DialogDescription>Configure implementation settings for {website.url}.</DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-4">
            <div className="space-y-2">
              <span className="micro-label block">Default currency</span>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger className={SELECT_TRIGGER + ' w-[200px]'}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <ObjectiveSelect websiteId={website.id} value={conversionEvent} onChange={setConversionEvent} />

            <div className="space-y-2">
              <div>
                <span className="micro-label block">Tracking Method</span>
                <p className="supporting m-0 mt-1">
                  Choose how you want to track conversions and events for this website.
                </p>
              </div>

              <RadioGroup value={trackingMethod} onValueChange={(v) => choice.choose(v as TrackingMethod)}>
                {choice.options.map((method) => (
                  <label
                    key={method.value}
                    className={cn(
                      'rounded-card flex items-start gap-3 border p-3 transition-colors duration-150',
                      method.readOnly ? 'cursor-default' : 'cursor-pointer',
                      trackingMethod === method.value
                        ? 'bg-sel-bg border-sel-line'
                        : 'border-line bg-panel2 hover:bg-hover',
                    )}
                  >
                    <RadioGroupItem value={method.value} disabled={method.readOnly} className="mt-0.5" />
                    <div>
                      <div className="text-foreground text-meta font-medium">{method.label}</div>
                      <div className="supporting">{method.description}</div>
                    </div>
                  </label>
                ))}
              </RadioGroup>

              {/* The Google identifiers used to be table columns; they are diagnostics
                read once in a while, so they live here with the tracking choice now. */}
              {(website.ganPropertyId || website.ganMeasurementId) && (
                <p className="supporting m-0">
                  Linked Google property: <span className="num">{website.ganPropertyId ?? '—'}</span>
                  {website.ganMeasurementId && (
                    <>
                      {' · '}
                      <span className="num">{website.ganMeasurementId}</span>
                    </>
                  )}
                </p>
              )}
            </div>

            <div className="flex items-start justify-between gap-4">
              <div>
                <label htmlFor={`visitor-id-${website.id}`} className="micro-label block cursor-pointer">
                  Visitor ID
                </label>
                <p className="supporting m-0 mt-1">
                  Stores a random ID in each visitor's browser so their events can be linked. The ID holds no personal
                  details itself, but as a lasting identifier it counts as personal data: make sure your consent banner
                  and privacy policy cover it. Turning it off stops the ID being sent; IDs already stored stay in
                  browsers.
                </p>
              </div>
              <Switch
                id={`visitor-id-${website.id}`}
                checked={visitorIdEnabled}
                onCheckedChange={setVisitorIdEnabled}
                className="mt-0.5 shrink-0"
              />
            </div>

            <div className="flex items-start justify-between gap-4">
              <div>
                <label htmlFor={`learnings-${website.id}`} className="micro-label block cursor-pointer">
                  Learnings
                </label>
                <p className="supporting m-0 mt-1">
                  Labels each concluded test and writes one sentence on what it taught, using your account's AI credits.
                  Turning it on covers tests that end from now on; past tests are written only if you ask.
                  {!canManageLearnings && ' Only an admin can change this.'}
                </p>
              </div>
              <Switch
                id={`learnings-${website.id}`}
                checked={learningsEnabled}
                onCheckedChange={setLearningsEnabled}
                disabled={!canManageLearnings}
                className="mt-0.5 shrink-0"
              />
            </div>

            {learningsEnabled && (
              <div className="flex items-start justify-between gap-4">
                <div>
                  <label htmlFor={`learnings-screenshots-${website.id}`} className="micro-label block cursor-pointer">
                    Read variant screenshots
                  </label>
                  <p className="supporting m-0 mt-1">
                    Shows each variant's screenshots to the AI, for a more precise account of what changed. Off is
                    cheaper and vaguer.
                  </p>
                </div>
                <Switch
                  id={`learnings-screenshots-${website.id}`}
                  checked={readScreenshots}
                  onCheckedChange={setReadScreenshots}
                  disabled={!canManageLearnings}
                  className="mt-0.5 shrink-0"
                />
              </div>
            )}

            {/* The saved switch, not the one being edited: the status behind it only exists once
              learnings are on, and its count is of tests from before that moment. */}
            {website.learningsEnabled && canManageLearnings && <LearningsPastTests websiteId={website.id} />}

            {/* Two levels: the account's switch (Settings, General) governs every website it
              owns, and this one takes a single website out. Disabled rather than hidden
              when the account has opted out, so the state is never a mystery. */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <label htmlFor={`learnings-contribution-${website.id}`} className="micro-label block cursor-pointer">
                  Contribute learnings
                </label>
                <p className="supporting m-0 mt-1">
                  {accountOptedOut
                    ? 'The account that owns this website has opted out, so nothing from it is shared.'
                    : "Lets this website's learnings feed Pertento's global intelligence, which learns what kinds of change work across websites. Shared: change types, page types, the learning sentence, the outcome and the lift. Never your pages, code, screenshots, hypotheses, names or URLs. Your own learnings are unaffected."}
                  {!accountOptedOut && !canManageLearnings && ' Only an admin can change this.'}
                </p>
              </div>
              <Switch
                id={`learnings-contribution-${website.id}`}
                checked={!accountOptedOut && contributesLearnings}
                onCheckedChange={setContributesLearnings}
                disabled={accountOptedOut || !canManageLearnings}
                className="mt-0.5 shrink-0"
              />
            </div>
            <DataFeedsSection websiteId={website.id} />
          </div>

          <DialogFooter>
            <Button onClick={handleSave} className="h-9 rounded-lg">
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ServerTrackingConfirm choice={choice} />
    </>
  );
};
